import React, { useState, useEffect } from "react";
import { FileText, Wrench, UserCheck, BarChart3, Headphones, Loader2, X, Copy, Check, Upload, AlertTriangle, Phone, Mail, FolderLock } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

const DOC_TYPES = [
  ["bail", "Contrat de bail"], ["avenant", "Avenant au bail"], ["relance", "Lettre de relance"],
  ["conge", "Lettre de congé"], ["quittance", "Quittance de loyer"], ["mise_en_demeure", "Mise en demeure"],
];
const URG_COLORS = { Critique: "bg-red-100 text-red-700", "Élevée": "bg-orange-100 text-orange-700", Moyenne: "bg-amber-100 text-amber-700", Faible: "bg-emerald-100 text-emerald-700" };
const inp = "w-full px-3 py-2 rounded-xl border border-brand-soft bg-white outline-none focus:border-brand-lime text-sm text-brand-greenDeep";

const toBase64 = (file) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onload = () => res(String(r.result).split(",")[1]);
  r.onerror = rej;
  r.readAsDataURL(file);
});

export default function AiTools() {
  const [tool, setTool] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  // form states
  const [docType, setDocType] = useState("relance");
  const [docCtx, setDocCtx] = useState("");
  const [diagDesc, setDiagDesc] = useState("");
  const [diagImg, setDiagImg] = useState("");
  const [app, setApp] = useState({ applicant_name: "", monthly_income: "", contract_type: "CDI", rent: "", guarantor: false });
  const [appImg, setAppImg] = useState("");
  const [handoff, setHandoff] = useState({ message: "", name: "", email: "", phone: "" });
  const [properties, setProperties] = useState([]);
  const [saveProp, setSaveProp] = useState("");
  const [savedTo, setSavedTo] = useState(false);

  useEffect(() => { api.get("/properties").then((r) => setProperties(r.data || [])).catch(() => {}); }, []);

  const saveToVault = () => run(async () => {
    if (!saveProp) { toast.error("Choisissez un bien"); return; }
    await api.post("/ai/save-document", { property_id: saveProp, doc_type: docType, content: result.content });
    setSavedTo(true);
    toast.success("Document enregistré dans le coffre-fort");
  });

  const open = (t) => { setTool(t); setResult(null); setCopied(false); };
  const close = () => { setTool(null); setResult(null); };

  const run = async (fn) => { setBusy(true); try { await fn(); } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); } finally { setBusy(false); } };

  const genDoc = () => run(async () => {
    const r = await api.post("/ai/generate-document", { doc_type: docType, context: docCtx });
    setResult({ kind: "doc", content: r.data.content });
    setSavedTo(false);
  });
  const diagnose = () => run(async () => {
    const r = await api.post("/ai/diagnose-work", { description: diagDesc, image_base64: diagImg });
    setResult({ kind: "diag", ...r.data });
  });
  const analyze = () => run(async () => {
    const r = await api.post("/ai/analyze-application", { ...app, monthly_income: Number(app.monthly_income) || 0, rent: Number(app.rent) || 0, document_base64: appImg });
    setResult({ kind: "app", ...r.data });
  });
  const report = () => run(async () => {
    const r = await api.post("/ai/financial-report");
    setResult({ kind: "report", ...r.data });
  });
  const exportReportPdf = () => run(async () => {
    const r = await api.post("/ai/financial-report/pdf", {
      kpis: result.kpis, summary: result.summary, highlights: result.highlights,
      market_comparison: result.market_comparison, recommendations: result.recommendations,
    }, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url; a.download = "rapport-gestion-360immoinvest.pdf"; a.click();
    window.URL.revokeObjectURL(url);
    toast.success("PDF téléchargé");
  });
  const sendHandoff = () => run(async () => {
    if (!handoff.message.trim()) { toast.error("Écrivez votre message"); return; }
    const r = await api.post("/ai/human-handoff", handoff);
    toast.success(`Message transmis à ${r.data.support_email}`);
    close();
  });

  const copy = (txt) => { navigator.clipboard.writeText(txt); setCopied(true); toast.success("Copié"); setTimeout(() => setCopied(false), 1500); };

  const TOOLS = [
    ["doc", "Générer un document", FileText, "Bail, avenant, relance, congé…"],
    ["diag", "Diagnostic travaux IA", Wrench, "Photo + description → type & urgence"],
    ["app", "Analyser un dossier", UserCheck, "Solvabilité & cohérence des pièces"],
    ["report", "Rapport financier IA", BarChart3, "Compte-rendu de gestion automatisé"],
    ["handoff", "Parler à un humain", Headphones, "Transfert à un conseiller"],
  ];

  return (
    <div className="bg-white rounded-2xl border border-brand-soft p-6 overflow-y-auto" data-testid="ai-tools">
      <h3 className="font-semibold text-brand-greenDeep mb-1">Outils IA</h3>
      <p className="text-xs text-brand-green/50 mb-4">Cliquez pour utiliser</p>
      <div className="space-y-2">
        {TOOLS.map(([id, label, Icon, desc]) => (
          <button key={id} onClick={() => open(id)} className="w-full text-left px-3 py-3 rounded-xl bg-brand-softer hover:bg-brand-soft transition-colors flex items-start gap-3" data-testid={`ai-tool-${id}`}>
            <Icon className="w-5 h-5 text-brand-greenDeep mt-0.5 flex-shrink-0" />
            <div><p className="text-sm font-semibold text-brand-greenDeep">{label}</p><p className="text-xs text-brand-green/50">{desc}</p></div>
          </button>
        ))}
      </div>

      {tool && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={close}>
          <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-7" onClick={(e) => e.stopPropagation()} data-testid="ai-tool-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-xl font-semibold text-brand-greenDeep">{TOOLS.find((t) => t[0] === tool)[1]}</h3>
              <button onClick={close} className="p-2 rounded-full hover:bg-brand-softer"><X className="w-5 h-5 text-brand-green/60" /></button>
            </div>

            {/* Document */}
            {tool === "doc" && (
              <div className="space-y-3">
                <select value={docType} onChange={(e) => setDocType(e.target.value)} className={inp} data-testid="doc-type-select">
                  {DOC_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <textarea value={docCtx} onChange={(e) => setDocCtx(e.target.value)} rows={3} className={inp} placeholder="Contexte : noms, dates, montants, adresse…" data-testid="doc-context" />
                <button onClick={genDoc} disabled={busy} className="w-full py-2.5 rounded-full bg-brand-greenDeep text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="doc-generate-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />} Générer</button>
                {result?.kind === "doc" && (
                  <div className="mt-2" data-testid="doc-result">
                    <div className="flex justify-end mb-1"><button onClick={() => copy(result.content)} className="text-xs inline-flex items-center gap-1 text-brand-greenDeep font-medium">{copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} Copier</button></div>
                    <pre className="whitespace-pre-wrap text-sm text-brand-green/80 bg-brand-softer rounded-xl p-4 max-h-72 overflow-y-auto font-sans">{result.content}</pre>
                    <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-brand-soft pt-3">
                      <FolderLock className="w-4 h-4 text-brand-greenDeep" />
                      <span className="text-sm text-brand-green/70">Enregistrer au coffre-fort d'un bien :</span>
                      <select value={saveProp} onChange={(e) => setSaveProp(e.target.value)} className="px-3 py-1.5 rounded-lg border border-brand-soft text-sm bg-white outline-none focus:border-brand-lime" data-testid="save-doc-property">
                        <option value="">— Choisir un bien —</option>
                        {properties.map((p) => <option key={p.id} value={p.id}>{p.name || p.address}</option>)}
                      </select>
                      <button onClick={saveToVault} disabled={busy || savedTo} className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep text-sm font-semibold disabled:opacity-60" data-testid="save-doc-btn">
                        {savedTo ? <><Check className="w-4 h-4" /> Enregistré</> : <><FolderLock className="w-4 h-4" /> Enregistrer</>}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Diagnostic */}
            {tool === "diag" && (
              <div className="space-y-3">
                <textarea value={diagDesc} onChange={(e) => setDiagDesc(e.target.value)} rows={3} className={inp} placeholder="Décrivez le problème (ex: fuite sous l'évier…)" data-testid="diag-desc" />
                <label className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-dashed border-brand-soft cursor-pointer text-sm text-brand-green/60 hover:border-brand-lime">
                  <Upload className="w-4 h-4" /> {diagImg ? "Photo ajoutée ✓" : "Ajouter une photo (optionnel)"}
                  <input type="file" accept="image/*" className="hidden" data-testid="diag-photo" onChange={async (e) => { const f = e.target.files?.[0]; if (f) setDiagImg(await toBase64(f)); }} />
                </label>
                <button onClick={diagnose} disabled={busy} className="w-full py-2.5 rounded-full bg-brand-greenDeep text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="diag-run-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wrench className="w-4 h-4" />} Diagnostiquer</button>
                {result?.kind === "diag" && (
                  <div className="mt-2 space-y-3" data-testid="diag-result">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold px-3 py-1 rounded-full bg-brand-lime/30 text-brand-greenDeep">{result.category}</span>
                      <span className={`text-xs font-bold px-3 py-1 rounded-full ${URG_COLORS[result.urgency] || "bg-brand-softer"}`}>Urgence : {result.urgency}</span>
                    </div>
                    <p className="text-sm text-brand-green/80">{result.summary}</p>
                    {result.advice && <p className="text-sm text-brand-green/60 flex gap-2"><AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />{result.advice}</p>}
                    <div>
                      <p className="text-sm font-semibold text-brand-greenDeep mb-2">Artisans suggérés</p>
                      {result.suggested_artisans?.length ? result.suggested_artisans.map((a) => (
                        <div key={a.id} className="flex items-center justify-between gap-2 px-3 py-2 rounded-xl bg-brand-softer mb-1.5 text-sm">
                          <div><p className="font-semibold text-brand-greenDeep">{a.company || a.name}</p><p className="text-xs text-brand-green/50">{a.domain} · {a.city}</p></div>
                          <div className="flex gap-2 text-brand-greenDeep">{a.phone && <a href={`tel:${a.phone}`} title={a.phone}><Phone className="w-4 h-4" /></a>}{a.email && <a href={`mailto:${a.email}`}><Mail className="w-4 h-4" /></a>}</div>
                        </div>
                      )) : <p className="text-xs text-brand-green/45">Aucun artisan {result.category} référencé pour l'instant.</p>}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Analyse dossier */}
            {tool === "app" && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <input value={app.applicant_name} onChange={(e) => setApp({ ...app, applicant_name: e.target.value })} className={inp} placeholder="Nom du candidat" data-testid="app-name" />
                  <input value={app.contract_type} onChange={(e) => setApp({ ...app, contract_type: e.target.value })} className={inp} placeholder="Type de contrat" />
                  <input type="number" value={app.monthly_income} onChange={(e) => setApp({ ...app, monthly_income: e.target.value })} className={inp} placeholder="Revenu mensuel net (€)" data-testid="app-income" />
                  <input type="number" value={app.rent} onChange={(e) => setApp({ ...app, rent: e.target.value })} className={inp} placeholder="Loyer visé (CC €)" data-testid="app-rent" />
                </div>
                <label className="flex items-center gap-2 text-sm text-brand-green/70"><input type="checkbox" checked={app.guarantor} onChange={(e) => setApp({ ...app, guarantor: e.target.checked })} className="w-4 h-4 accent-brand-limeDark" /> Présence d'un garant</label>
                <label className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-dashed border-brand-soft cursor-pointer text-sm text-brand-green/60 hover:border-brand-lime">
                  <Upload className="w-4 h-4" /> {appImg ? "Justificatif ajouté ✓" : "Joindre un justificatif (optionnel)"}
                  <input type="file" accept="image/*" className="hidden" onChange={async (e) => { const f = e.target.files?.[0]; if (f) setAppImg(await toBase64(f)); }} />
                </label>
                <button onClick={analyze} disabled={busy} className="w-full py-2.5 rounded-full bg-brand-greenDeep text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="app-run-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserCheck className="w-4 h-4" />} Analyser</button>
                {result?.kind === "app" && (
                  <div className="mt-2 space-y-3" data-testid="app-result">
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div className="bg-brand-softer rounded-xl px-2 py-3"><p className="text-xs text-brand-green/45">Solvabilité</p><p className="font-semibold text-brand-greenDeep">{result.solvency}</p></div>
                      <div className="bg-brand-softer rounded-xl px-2 py-3"><p className="text-xs text-brand-green/45">Taux d'effort</p><p className="font-semibold text-brand-greenDeep">{result.effort_rate}%</p></div>
                      <div className="bg-brand-softer rounded-xl px-2 py-3"><p className="text-xs text-brand-green/45">Cohérence</p><p className="font-semibold text-brand-greenDeep">{result.coherence}</p></div>
                    </div>
                    {result.fraud_flags?.length > 0 && (
                      <div className="rounded-xl bg-red-50 border border-red-200 p-3">
                        <p className="text-sm font-semibold text-red-700 flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> Points d'alerte</p>
                        <ul className="list-disc pl-5 text-sm text-red-700 mt-1">{result.fraud_flags.map((f, i) => <li key={i}>{f}</li>)}</ul>
                      </div>
                    )}
                    <p className="text-sm text-brand-green/80 leading-relaxed">{result.recommendation}</p>
                  </div>
                )}
              </div>
            )}

            {/* Rapport financier */}
            {tool === "report" && (
              <div className="space-y-3">
                <p className="text-sm text-brand-green/60">Génère un compte-rendu de gestion à partir de vos opérations financières.</p>
                <button onClick={report} disabled={busy} className="w-full py-2.5 rounded-full bg-brand-greenDeep text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="report-run-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <BarChart3 className="w-4 h-4" />} Générer le rapport</button>
                {result?.kind === "report" && (
                  <div className="mt-2 space-y-3" data-testid="report-result">
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div className="bg-emerald-50 rounded-xl px-2 py-3"><p className="text-xs text-brand-green/45">Revenus</p><p className="font-semibold text-emerald-700">{result.kpis.revenus.toLocaleString("fr-FR")} €</p></div>
                      <div className="bg-red-50 rounded-xl px-2 py-3"><p className="text-xs text-brand-green/45">Charges</p><p className="font-semibold text-red-600">{result.kpis.charges.toLocaleString("fr-FR")} €</p></div>
                      <div className="bg-brand-lime/20 rounded-xl px-2 py-3"><p className="text-xs text-brand-green/45">Net</p><p className="font-semibold text-brand-greenDeep">{result.kpis.net.toLocaleString("fr-FR")} €</p></div>
                    </div>
                    <p className="text-sm text-brand-green/80 leading-relaxed">{result.summary}</p>
                    {result.highlights?.length > 0 && <ul className="list-disc pl-5 text-sm text-brand-green/70 space-y-1">{result.highlights.map((h, i) => <li key={i}>{h}</li>)}</ul>}
                    {result.market_comparison && <p className="text-sm text-brand-green/60 italic">📊 {result.market_comparison}</p>}
                    {result.recommendations?.length > 0 && (
                      <div><p className="text-sm font-semibold text-brand-greenDeep">Recommandations</p><ul className="list-disc pl-5 text-sm text-brand-green/70 space-y-1 mt-1">{result.recommendations.map((r, i) => <li key={i}>{r}</li>)}</ul></div>
                    )}
                    <button onClick={exportReportPdf} disabled={busy} className="mt-2 w-full py-2.5 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="report-pdf-btn"><FileText className="w-4 h-4" /> Exporter en PDF</button>
                  </div>
                )}
              </div>
            )}

            {/* Parler à un humain */}
            {tool === "handoff" && (
              <div className="space-y-3">
                <p className="text-sm text-brand-green/60">Un conseiller vous recontactera. Votre message est transmis par email au support.</p>
                <div className="grid grid-cols-2 gap-3">
                  <input value={handoff.name} onChange={(e) => setHandoff({ ...handoff, name: e.target.value })} className={inp} placeholder="Votre nom" />
                  <input value={handoff.phone} onChange={(e) => setHandoff({ ...handoff, phone: e.target.value })} className={inp} placeholder="Téléphone" />
                </div>
                <input value={handoff.email} onChange={(e) => setHandoff({ ...handoff, email: e.target.value })} className={inp} placeholder="Email de contact" />
                <textarea value={handoff.message} onChange={(e) => setHandoff({ ...handoff, message: e.target.value })} rows={3} className={inp} placeholder="Votre message…" data-testid="handoff-message" />
                <button onClick={sendHandoff} disabled={busy} className="w-full py-2.5 rounded-full bg-brand-greenDeep text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="handoff-send-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Headphones className="w-4 h-4" />} Envoyer au conseiller</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
